import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getStatusDisplay } from "../../../../anyspend/index.js";
import { useSearchParams } from "../../../../shared/react/index.js";
import { AlertCircle, AlertTriangle, CheckCircle, Clock, Info, Loader2, RotateCcw, X } from "lucide-react";
import { useToastContext } from "../../../../global-account/react/index.js";
import { memo, useEffect, useRef } from "react";
import { useAnySpendCustomization } from "../context/AnySpendCustomizationContext.js";
import { AnimatedCheckmark } from "../icons/AnimatedCheckmark.js";
import { StepProgress } from "./StepProgress.js";
/** Map order status to its step index in the StepProgress. -1 = not a step state. */
function getStepIndex(status) {
  if (["waiting_stripe_payment", "scanning_deposit_transaction"].includes(status)) return 0;
  if (["quoting_after_deposit", "sending_token_from_vault", "relay", "executing"].includes(status)) return 1;
  return -1;
}
function getToastIcon(type) {
  const iconClass = "h-5 w-5";
  switch (type) {
    case "success":
      return _jsx(CheckCircle, {
        className: `${iconClass} text-green-500`
      });
    case "error":
      return _jsx(AlertCircle, {
        className: `${iconClass} text-red-500`
      });
    case "info":
      return _jsx(Info, {
        className: `${iconClass} text-blue-500`
      });
    case "warning":
      return _jsx(AlertTriangle, {
        className: `${iconClass} text-amber-500`
      });
  }
}
export const OrderStatus = memo(function OrderStatus({
  order,
  selectedCryptoPaymentMethod
}) {
  const {
    text: defaultText,
    description: defaultDescription
  } = getStatusDisplay(order);
  const {
    content,
    slots
  } = useAnySpendCustomization();
  const searchParams = useSearchParams();
  const cryptoPaymentMethod = selectedCryptoPaymentMethod || searchParams.get("cryptoPaymentMethod");
  const currentStepIndex = getStepIndex(order.status);
  const prevStepIndexRef = useRef(currentStepIndex);
  const shouldAnimateCheck = currentStepIndex > prevStepIndexRef.current;
  useEffect(() => {
    prevStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);
  // Resolve content overrides based on order status
  let text = defaultText;
  let description = defaultDescription;
  if (order.status === "executed") {
    if (content.successTitle && typeof content.successTitle === "string") text = content.successTitle;
    if (content.successDescription) description = content.successDescription;
  } else if (order.status === "failure") {
    if (content.failureTitle && typeof content.failureTitle === "string") text = content.failureTitle;
    if (content.failureDescription) description = content.failureDescription;
  } else if (order.status === "expired") {
    if (content.expiredTitle && typeof content.expiredTitle === "string") text = content.expiredTitle;
    if (content.expiredDescription) description = content.expiredDescription;
  } else if (order.status === "refunded") {
    if (content.refundedTitle && typeof content.refundedTitle === "string") text = content.refundedTitle;
    if (content.refundedDescription) description = content.refundedDescription;
  } else if (content.processingTitle || content.processingDescription) {
    if (content.processingTitle && typeof content.processingTitle === "string") text = content.processingTitle;
    if (content.processingDescription) description = content.processingDescription;
  }
  const {
    latestToast
  } = useToastContext();
  // Override subtitle with toast notification when present (title stays unchanged)
  let notificationIcon = undefined;
  if (latestToast && currentStepIndex >= 0) {
    description = latestToast.message;
    notificationIcon = getToastIcon(latestToast.type);
  }
  const paymentSteps = [{
    id: 1,
    title: text,
    description: typeof description === "string" ? description : defaultDescription || "",
    icon: notificationIcon
  }, {
    id: 2,
    title: text,
    description: typeof description === "string" ? description : defaultDescription || "",
    icon: notificationIcon
  }];
  if (currentStepIndex === 0) {
    if (!(order.status === "scanning_deposit_transaction" && cryptoPaymentMethod === "transfer_crypto")) {
      return _jsx(StepProgress, {
        steps: paymentSteps,
        currentStepIndex: 0
      });
    }
  }
  if (currentStepIndex === 1) {
    return _jsx(StepProgress, {
      steps: paymentSteps,
      currentStepIndex: 1,
      animateCompletedSteps: shouldAnimateCheck
    });
  }
  if (order.status === "refunding") {
    return _jsx("div", {
      className: "flex items-center justify-center gap-2",
      children: _jsxs("div", {
        className: "flex flex-col items-center",
        children: [_jsx("div", {
          className: "flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15",
          children: _jsx(Loader2, {
            className: "h-6 w-6 animate-spin text-amber-500"
          })
        }), _jsx("h2", {
          className: "text-as-primary mt-4 text-xl font-semibold",
          children: text
        }), _jsx("div", {
          className: "text-as-tertiary mt-1 text-center text-sm",
          children: description
        })]
      })
    });
  }
  if (selectedCryptoPaymentMethod === "transfer_crypto" && order.status === "scanning_deposit_transaction") {
    return null;
  }
  if (order.status === "executed") {
    return _jsx("div", {
      className: "flex items-center justify-center gap-2",
      children: _jsxs("div", {
        className: "flex flex-col items-center",
        children: [_jsx(AnimatedCheckmark, {
          className: "h-14 w-14"
        }), _jsx("h2", {
          className: "text-as-primary mt-4 text-xl font-semibold",
          children: content.successTitle || text
        }), _jsx("div", {
          className: "text-as-tertiary mt-1 text-center text-sm",
          children: content.successDescription || description
        })]
      })
    });
  }
  if (order.status === "expired") {
    if (slots.errorScreen) {
      return _jsx(_Fragment, {
        children: slots.errorScreen({
          title: text,
          description: typeof description === "string" ? description : defaultDescription || "",
          errorType: "expired",
          orderId: order.id
        })
      });
    }
    return _jsx("div", {
      className: "flex items-center justify-center gap-2",
      children: _jsxs("div", {
        className: "flex flex-col items-center",
        children: [_jsx("div", {
          className: "flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15",
          children: _jsx(Clock, {
            className: "h-5 w-5 text-amber-500"
          })
        }), _jsx("h2", {
          className: "text-as-primary mt-4 text-xl font-semibold",
          children: text
        }), _jsx("div", {
          className: "text-as-tertiary mt-1 text-center text-sm",
          children: description
        })]
      })
    });
  }
  if (order.status === "refunded") {
    if (slots.errorScreen) {
      return _jsx(_Fragment, {
        children: slots.errorScreen({
          title: text,
          description: typeof description === "string" ? description : defaultDescription || "",
          errorType: "refunded",
          orderId: order.id
        })
      });
    }
    return _jsx("div", {
      className: "flex items-center justify-center gap-2",
      children: _jsxs("div", {
        className: "flex flex-col items-center",
        children: [_jsx("div", {
          className: "bg-as-surface-secondary flex h-10 w-10 items-center justify-center rounded-full",
          children: _jsx(RotateCcw, {
            className: "text-as-secondary h-5 w-5"
          })
        }), _jsx("h2", {
          className: "text-as-primary mt-4 text-xl font-semibold",
          children: text
        }), _jsx("div", {
          className: "text-as-tertiary mt-1 text-center text-sm",
          children: description
        })]
      })
    });
  }
  if (slots.errorScreen) {
    return _jsx(_Fragment, {
      children: slots.errorScreen({
        title: text,
        description: typeof description === "string" ? description : defaultDescription || "",
        errorType: "failure",
        orderId: order.id
      })
    });
  }
  return _jsx("div", {
    className: "flex items-center justify-center gap-2",
    children: _jsxs("div", {
      className: "flex flex-col items-center",
      children: [_jsx("div", {
        className: "bg-as-error-secondary flex h-10 w-10 items-center justify-center rounded-full",
        children: _jsx(X, {
          className: "text-as-content-icon-error h-5 w-5"
        })
      }), _jsx("h2", {
        className: "text-as-primary mt-4 text-xl font-semibold",
        children: text
      }), _jsx("div", {
        className: "text-as-tertiary mt-1 text-center text-sm",
        style: {
          whiteSpace: "normal"
        },
        children: description
      })]
    })
  });
});